import api from './api';
import { ApiResponse, PageResponse } from '../types/api';
import { MovimentacaoCaixaRequestDTO, MovimentacaoCaixaResponseDTO, CaixaResumoDTO } from '../types/caixa';

// Bases de endpoints (Movimentações de Caixa)
const movementsBaseUrl = process.env.REACT_APP_MOVIMENTACOES_CAIXA_URL ?? '/v1/movimentacoes-caixa';
// Rota oficial dos tipos de movimentação
const movementTypesCandidateUrl = '/v1/movimentacoes-caixa/tipos-movimentacao';

export const caixaService = {
  // Normalização de Caixa removida — alinhado ao OpenAPI atual

  normalizeMovement(raw: any): MovimentacaoCaixaResponseDTO {
    const tipoRaw = raw.tipoMovimentacao ?? raw.tipo ?? raw.type;
    const tipoMovimentacao = typeof tipoRaw === 'string' ? tipoRaw : (tipoRaw?.nome ?? tipoRaw?.descricao ?? tipoRaw?.codigo ?? '');
    return {
      id: raw.id,
      tipoMovimentacao,
      valor: Number(raw.valor ?? 0),
      dataMovimentacao: raw.dataMovimentacao ?? raw.dataMovimento ?? raw.data ?? raw.createdAt,
      descricao: raw.descricao,
      categoria: raw.categoria,
      vendaId: raw.vendaId,
      vendaNumero: raw.vendaNumero,
      compraId: raw.compraId,
      compraNumero: raw.compraNumero,
      usuarioId: raw.usuarioId,
      usuarioNome: raw.usuarioNome,
      observacoes: raw.observacoes ?? raw.obs ?? raw.description,
      tenantId: raw.tenantId,
      dataCriacao: raw.dataCriacao ?? raw.createdAt,
      dataAtualizacao: raw.dataAtualizacao ?? raw.updatedAt,
    } as MovimentacaoCaixaResponseDTO;
  },

  // Listar movimentações com paginação
  async list(params?: { page?: number; size?: number; sort?: string; tipoMovimentacao?: string }): Promise<ApiResponse<PageResponse<MovimentacaoCaixaResponseDTO>>> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) searchParams.append('page', String(params.page));
    if (params?.size !== undefined) searchParams.append('size', String(params.size));
    if (params?.sort) searchParams.append('sort', params.sort);
    if (params?.tipoMovimentacao) searchParams.append('tipoMovimentacao', params.tipoMovimentacao);

    const qs = searchParams.toString();
    const url = qs ? `${movementsBaseUrl}?${qs}` : movementsBaseUrl;

    const response = await api.get<ApiResponse<PageResponse<any>>>(url);
    const original = response.data;
    const mapped: PageResponse<MovimentacaoCaixaResponseDTO> = {
      ...(original.data ?? original),
      content: ((original.data ?? original)?.content ?? []).map((x: any) => this.normalizeMovement(x)),
    } as PageResponse<MovimentacaoCaixaResponseDTO>;

    return {
      success: true,
      message: 'Movimentações carregadas',
      data: mapped,
      timestamp: new Date().toISOString(),
    };
  },

  // Buscar movimentação por ID
  async getById(id: string): Promise<ApiResponse<MovimentacaoCaixaResponseDTO>> {
    const response = await api.get<ApiResponse<any>>(`${movementsBaseUrl}/${encodeURIComponent(id)}`);
    const raw = response.data?.data ?? response.data;
    return {
      success: true,
      message: 'Movimentação carregada',
      data: this.normalizeMovement(raw),
      timestamp: new Date().toISOString(),
    };
  },

  // Resumo simples combinando saldo atual e movimentações recentes
  async getResumo(): Promise<ApiResponse<CaixaResumoDTO>> {
    // Saldo atual (mapa com chave -> BigDecimal)
    const saldoResp = await this.getSaldoAtual();

    // Movimentações recentes (usa endpoint dedicado)
    const recentResp = await api.get<ApiResponse<any>>(`${movementsBaseUrl}/recentes?size=100`);
    const original = recentResp.data;
    const lista = ((original?.data ?? original)?.content ?? (Array.isArray(original?.data ?? original) ? (original?.data ?? original) : [])) as any[];
    const movimentos = Array.isArray(lista) ? lista.map((x) => this.normalizeMovement(x)) : [];

    // Classificação básica por tipo para calcular entradas/saídas
    const entradasTipos = new Set<string>([
      'Abertura de Caixa',
      'Entrada Diversa',
      'Depósito',
      'Transferência Recebida',
      'Juros Recebidos',
      'Desconto Obtido',
      'Devolução de Compra',
    ]);
    const saidasTipos = new Set<string>([
      'Fechamento de Caixa',
      'Pagamento a Fornecedor',
      'Pagamento de Despesa',
      'Saque',
      'Transferência Enviada',
      'Devolução a Cliente',
      'Saída Diversa',
      'Taxa Bancária',
      'Juros Pagos',
      'Multa Paga',
    ]);

    const totalEntradas = movimentos
      .filter((m) => entradasTipos.has(m.tipoMovimentacao))
      .reduce((sum, m) => sum + Number(m.valor ?? 0), 0);
    const totalSaidas = movimentos
      .filter((m) => saidasTipos.has(m.tipoMovimentacao))
      .reduce((sum, m) => sum + Number(m.valor ?? 0), 0);

    const resumo: CaixaResumoDTO = {
      saldoAtual: Number(saldoResp.data ?? 0),
      totalEntradas,
      totalSaidas,
      movimentacoesHoje: movimentos.length,
    };

    return {
      success: true,
      message: 'Resumo do caixa carregado',
      data: resumo,
      timestamp: new Date().toISOString(),
    };
  },

  // Listar movimentações com filtros opcionais
  async listMovements(params: { page?: number; size?: number; tipoMovimentacao?: string; caixaId?: string }): Promise<ApiResponse<PageResponse<MovimentacaoCaixaResponseDTO>>> {
     const searchParams = new URLSearchParams();
     if (params.page !== undefined) searchParams.append('page', String(params.page));
     if (params.size !== undefined) searchParams.append('size', String(params.size));
     if (params.tipoMovimentacao) searchParams.append('tipoMovimentacao', params.tipoMovimentacao);
     if (params.caixaId) searchParams.append('caixaId', params.caixaId);

     const qs = searchParams.toString();
     const url = `${movementsBaseUrl}${qs ? `?${qs}` : ''}`;
     const response = await api.get<ApiResponse<PageResponse<any>>>(url);
     const original = response.data;
     const mapped: PageResponse<MovimentacaoCaixaResponseDTO> = {
       ...(original.data ?? original),
       content: ((original.data ?? original)?.content ?? []).map((x: any) => this.normalizeMovement(x)),
     } as PageResponse<MovimentacaoCaixaResponseDTO>;
     return {
       success: true,
       message: 'Movimentações carregadas',
       data: mapped,
       timestamp: new Date().toISOString(),
     };
   },

  // Criar movimentação de caixa
  async createMovement(data: MovimentacaoCaixaRequestDTO): Promise<ApiResponse<MovimentacaoCaixaResponseDTO>> {
    const payload = {
      ...data,
      dataMovimentacao: data.dataMovimentacao ?? new Date().toISOString().slice(0, 10),
    } as MovimentacaoCaixaRequestDTO;
    // Usa a rota correta de criação de movimentação
    const response = await api.post<ApiResponse<any>>('/v1/movimentacoes-caixa', payload);
    const raw = response.data?.data ?? response.data;
    return {
      success: true,
      message: 'Movimentação criada',
      data: this.normalizeMovement(raw),
      timestamp: new Date().toISOString(),
    };
  },

  // Abertura de caixa (wrapper)
  async openCaixa(valorInicial?: number, observacoes?: string): Promise<ApiResponse<MovimentacaoCaixaResponseDTO>> {
    const payload: MovimentacaoCaixaRequestDTO = {
      tipoMovimentacao: 'Abertura de Caixa',
      valor: Number(valorInicial ?? 0),
      dataMovimentacao: new Date().toISOString().slice(0, 10),
      observacoes,
    };
    return this.createMovement(payload);
  },

  // Fechamento de caixa (wrapper)
  async closeCaixa(observacoes?: string): Promise<ApiResponse<MovimentacaoCaixaResponseDTO>> {
    const payload: MovimentacaoCaixaRequestDTO = {
      tipoMovimentacao: 'Fechamento de Caixa',
      valor: 0,
      dataMovimentacao: new Date().toISOString().slice(0, 10),
      observacoes,
    };
    return this.createMovement(payload);
  },

  // Tipos de movimentação com fallback estático
  async getMovementTypes(): Promise<ApiResponse<string[]>> {
    try {
      const response = await api.get<ApiResponse<any>>(movementTypesCandidateUrl);
      const raw = response.data?.data ?? response.data;
      let tipos: string[] = [];

      if (Array.isArray(raw)) {
        tipos = raw.map((x: any) => {
          if (typeof x === 'string') return String(x).trim();
          // Prioriza código para alinhar com validação do backend
          return String(x?.codigo ?? x?.nome ?? x?.tipo ?? x?.descricao ?? x ?? '').trim();
        }).filter(Boolean);
      }

      if (tipos.length > 0) {
        return {
          success: true,
          message: 'Tipos de movimentação carregados',
          data: tipos,
          timestamp: new Date().toISOString(),
        };
      }

      throw new Error('Lista de tipos vazia');
    } catch (e) {
      // Fallback mantido vazio para evitar envio de tipos inválidos
      const fallback: string[] = [];
      return {
        success: true,
        message: 'Tipos de movimentação (fallback vazio) carregados',
        data: fallback,
        timestamp: new Date().toISOString(),
      };
    }
  },

  // Saldo atual (ApiResponseMapStringBigDecimal)
  async getSaldoAtual(caixaId?: string): Promise<ApiResponse<number>> {
    const qs = caixaId ? `?caixaId=${encodeURIComponent(caixaId)}` : '';
    const response = await api.get<ApiResponse<any>>(`${movementsBaseUrl}/saldo-atual${qs}`);
    const payload = response.data;
    const dataObj = payload?.data ?? payload;
    const keys = dataObj ? Object.keys(dataObj) : [];
    const value = dataObj?.saldo ?? (keys.length === 1 ? dataObj[keys[0]] : 0);
    return {
      success: true,
      message: 'Saldo atual obtido',
      data: Number(value ?? 0),
      timestamp: new Date().toISOString(),
    };
  },

  // Novo: obter movimentações recentes diretamente (aceita caixaId)
  async getRecentMovements(size: number = 50, caixaId?: string): Promise<MovimentacaoCaixaResponseDTO[]> {
    const params = new URLSearchParams();
    params.append('size', String(size));
    if (caixaId) params.append('caixaId', caixaId);
    const resp = await api.get<ApiResponse<any>>(`${movementsBaseUrl}/recentes?${params.toString()}`);
    const original = resp.data;
    const lista = ((original?.data ?? original)?.content ?? (Array.isArray(original?.data ?? original) ? (original?.data ?? original) : [])) as any[];
    return Array.isArray(lista) ? lista.map((x) => this.normalizeMovement(x)) : [];
  }
};

export default caixaService;